import React from 'react';
import { motion } from 'framer-motion';

const TermsOfService = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <div className="pt-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <motion.div {...fadeInUp}>
          <h1 className="text-4xl font-bold text-[#0B1320] font-poppins mb-8">
            Terms of Service
          </h1>
          
          <div className="prose prose-lg max-w-none text-[#4A4A4A] leading-relaxed space-y-8">
            <div>
              <p className="text-sm text-[#9E9E9E] mb-6">
                Last updated: January 2025
              </p>
              
              <p>
                Welcome to Sam's Nutri Hub. These Terms of Service ("Terms") govern your use of our website, 
                services, and any related applications (collectively, the "Services") operated by Sam's Nutri Hub 
                ("we," "us," or "our"). By accessing or using our Services, you agree to be bound by these Terms.
              </p>
            </div>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                Acceptance of Terms
              </h2>
              <p className="mb-6">
                By accessing and using our Services, you accept and agree to be bound by the terms and provision 
                of this agreement. If you do not agree to abide by the above, please do not use this service. 
                These Terms apply to all visitors, users, and others who access or use the Services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                Description of Services
              </h2>
              <p className="mb-4">
                Sam's Nutri Hub provides personalized nutrition and wellness services, including:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>Personalized nutrition consultations</li>
                <li>Custom meal planning and dietary guidance</li>
                <li>Fitness and wellness coaching</li>
                <li>Educational content and resources</li>
                <li>Health assessments and body analysis</li>
                <li>Ongoing support and monitoring</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                Medical Disclaimer
              </h2>
              <div className="bg-yellow-50 border border-yellow-200 p-6 rounded-lg mb-6">
                <p className="font-semibold text-yellow-800 mb-2">IMPORTANT MEDICAL DISCLAIMER:</p>
                <ul className="list-disc pl-6 space-y-2 text-yellow-800">
                  <li>Our services are for educational and informational purposes only</li>
                  <li>We do not provide medical diagnosis, treatment, or cure for any disease</li>
                  <li>Always consult with qualified healthcare professionals before making health decisions</li>
                  <li>Do not disregard professional medical advice or delay seeking treatment</li>
                  <li>Individual results may vary and are not guaranteed</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                User Responsibilities
              </h2>
              <p className="mb-4">
                By using our Services, you agree to:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>Provide accurate and complete information about your health status</li>
                <li>Follow recommendations responsibly and within your capabilities</li>
                <li>Inform us of any changes in your health condition</li>
                <li>Consult healthcare professionals for medical concerns</li>
                <li>Use the Services in accordance with applicable laws</li>
                <li>Respect intellectual property rights</li>
                <li>Not misuse or abuse our Services or systems</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                Payment Terms
              </h2>
              
              <h3 className="text-xl font-semibold text-[#0B1320] mb-3">
                Pricing and Payments
              </h3>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>All prices are in Indian Rupees (INR) unless otherwise stated</li>
                <li>Payment is due at the time of service booking</li>
                <li>We accept various payment methods as displayed during checkout</li>
                <li>Prices may change with prior notice</li>
              </ul>

              <h3 className="text-xl font-semibold text-[#0B1320] mb-3">
                Refund Policy
              </h3>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>Elite Wellness Portfolios include a 30-day satisfaction guarantee</li>
                <li>Refunds are available if no deviation from prescribed protocols is proven</li>
                <li>Consultation refunds may be available within specified timeframes</li>
                <li>Refund requests must be submitted in writing with supporting documentation</li>
                <li>Processing may take 7-14 business days</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                Intellectual Property Rights
              </h2>
              <p className="mb-4">
                The Services and their original content, features, and functionality are and will remain 
                the exclusive property of Sam's Nutri Hub and its licensors. The Services are protected 
                by copyright, trademark, and other laws. Our trademarks and trade dress may not be used 
                in connection with any product or service without our prior written consent.
              </p>
              
              <h3 className="text-xl font-semibold text-[#0B1320] mb-3">
                User Content
              </h3>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>You retain ownership of content you provide to us</li>
                <li>You grant us license to use your content for service delivery</li>
                <li>We may use anonymized data for research and improvement</li>
                <li>Testimonials and success stories may be used with your consent</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                Privacy and Data Protection
              </h2>
              <p className="mb-6">
                Your privacy is important to us. Our Privacy Policy explains how we collect, use, and protect 
                your information when you use our Services. By using our Services, you agree to the collection 
                and use of information in accordance with our Privacy Policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                Prohibited Uses
              </h2>
              <p className="mb-4">
                You may not use our Services:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>For any unlawful purpose or to solicit others to perform unlawful acts</li>
                <li>To violate any international, federal, provincial, or state regulations, rules, laws, or local ordinances</li>
                <li>To infringe upon or violate our intellectual property rights or the intellectual property rights of others</li>
                <li>To harass, abuse, insult, harm, defame, slander, disparage, intimidate, or discriminate</li>
                <li>To submit false or misleading information</li>
                <li>To upload or transmit viruses or any other type of malicious code</li>
                <li>To collect or track personal information of others without consent</li>
                <li>To spam, phish, pharm, pretext, spider, crawl, or scrape</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                Limitation of Liability
              </h2>
              <div className="bg-red-50 border border-red-200 p-6 rounded-lg mb-6">
                <p className="mb-4 font-semibold text-red-800">
                  IMPORTANT LIMITATION OF LIABILITY:
                </p>
                <p className="text-red-800 mb-4">
                  In no event shall Sam's Nutri Hub, nor its directors, employees, partners, agents, suppliers, 
                  or affiliates, be liable for any indirect, incidental, special, consequential, or punitive damages, 
                  including without limitation, loss of profits, data, use, goodwill, or other intangible losses, 
                  resulting from your use of the Services.
                </p>
                <p className="text-red-800">
                  Our total liability to you for all claims arising out of or relating to these Terms or our Services 
                  shall not exceed the amount paid by you to us in the twelve (12) months preceding the claim.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                Indemnification
              </h2>
              <p className="mb-6">
                You agree to defend, indemnify, and hold harmless Sam's Nutri Hub and its licensee and licensors, 
                and their employees, contractors, agents, officers and directors, from and against any and all claims, 
                damages, obligations, losses, liabilities, costs or debt, and expenses (including but not limited to attorney's fees).
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                Termination
              </h2>
              <p className="mb-4">
                We may terminate or suspend your account and bar access to the Services immediately, 
                without prior notice or liability, under our sole discretion, for any reason whatsoever 
                and without limitation, including but not limited to a breach of the Terms.
              </p>
              <p className="mb-6">
                If you wish to terminate your account, you may simply discontinue using the Services 
                and contact us to request account closure.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                Governing Law
              </h2>
              <p className="mb-6">
                These Terms shall be interpreted and governed by the laws of India and the State of Maharashtra, 
                without regard to its conflict of law provisions. Our failure to enforce any right or provision 
                of these Terms will not be considered a waiver of those rights.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                Changes to Terms
              </h2>
              <p className="mb-6">
                We reserve the right, at our sole discretion, to modify or replace these Terms at any time. 
                If a revision is material, we will provide at least 30 days notice prior to any new terms taking effect. 
                What constitutes a material change will be determined at our sole discretion.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                Contact Information
              </h2>
              <p className="mb-4">
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <div className="bg-[#F9FFF5] p-6 rounded-lg">
                <p><strong>Sam's Nutri Hub</strong></p>
                <p>Venkatesh Prime, 4th Floor</p>
                <p>Burudgaon Road, Ahmednagar, Maharashtra 414001</p>
                <p>Email: legal@samsnutrihub.com</p>
                <p>Phone: +91 98765 43210</p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                Severability
              </h2>
              <p className="mb-6">
                If any provision of these Terms is held to be unenforceable or invalid, such provision will be 
                changed and interpreted to accomplish the objectives of such provision to the greatest extent 
                possible under applicable law and the remaining provisions will continue in full force and effect.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-[#0B1320] font-poppins mb-4">
                Acknowledgment
              </h2>
              <p className="mb-6">
                By using our Services, you acknowledge that you have read these Terms of Service and agree to be bound by them. 
                These Terms constitute the entire agreement between you and Sam's Nutri Hub regarding the use of our Services.
              </p>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TermsOfService;